import chroma from "chroma-js";
import {
  engagementScore,
  platformRolesDisassociatedFromCompany,
  PRIVILEGE_ACTION,
  PRIVILEGE_SLUG,
  ROLES
} from "../constants";
import * as R from "ramda";
import dayjs from "dayjs";

import  utc from 'dayjs/plugin/utc';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { getCompany } from "services/company.service";
import { fileSizes } from "constants";
import { read, utils } from "xlsx";
import { getCompanyId  } from "services/identity.service";
import { getCampaignTemplates } from "services/campaign-template.service";
import { timezones } from "./timezones";
import moment from "moment";
import { getPositions } from "services/position.service";
import { wpm } from "../constants";
import { hasPrivilege } from "services/permission.service";
import {
  isMonday,
  startOfDay,
  nextMonday,
  isBefore,
} from "date-fns";
import Text from "atoms/Text";
import Heading from "atoms/Heading";
import { apiSlice } from "utils/api/baseSlice";
import { setCompanies, setSelectedCompany } from "@/redux/company-users";

dayjs.extend(utc)
dayjs.extend(isSameOrBefore)

export const normalizeOptionValue = (value) => (value || "").trim().toLowerCase();

/** First weekday on or after tomorrow, optionally capped by `estimatedJoiningDate`-style string. */
export const getNextValidWeekday = (joiningDeadlineStr) => {
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  currentDay.setDate(currentDay.getDate() + 1);

  const joiningDeadline = joiningDeadlineStr
    ? new Date(joiningDeadlineStr)
    : null;
  joiningDeadline?.setHours(0, 0, 0, 0);

  while (!joiningDeadline || currentDay <= joiningDeadline) {
    const dayOfWeek = currentDay.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) return new Date(currentDay);
    currentDay.setDate(currentDay.getDate() + 1);
  }
  return undefined;
};

export const hasSelectedAnyValidOption = (selectedOptions) =>
  Object.values(selectedOptions || {}).some((value) =>
    typeof value === "string" ? value.trim() !== "" : Boolean(value)
  );

export const findFirstDuplicateOptionIndex = (options = []) => {
  const seen = new Set();
  for (let index = 0; index < options.length; index++) {
    const normalized = normalizeOptionValue(options[index]?.value);
    if (!normalized) continue;
    if (seen.has(normalized)) return index;
    seen.add(normalized);
  }
  return -1;
};

/** Normalize API / RTK `message` values (strings, arrays, or objects) to a single display string. */
export const getErrorMessage = (message) => {
  if (Array.isArray(message) && message?.length > 0) {
    return message[0];
  }
  if (message?.name) return message.name;
  if (message?.errorMessage) return message.errorMessage;
  if (typeof message === "string") return message;
  return "Something went wrong";
}
  
/** Yes/No/Other survey: `true`/`false` OK; "Other" must be non-empty and not exactly yes/no/other (case-insensitive). */
export const isValidSurveyBooleanAnswerValue = (val) => {
  if (val === true || val === false) return true;
  if (typeof val !== "string") return false;
  const t = val.trim();
  if (!t) return false;
  return !["yes", "no", "other"].includes(t.toLowerCase());
};

export const formatUtcTimeToIST = (
  dateStr,
  timeStr,
  outFormat = "dddd, DD MMM YYYY hh:mm A"
) => {
  if (!dateStr || !timeStr) return "";
  const localDateTime = dayjs(`${dateStr} ${timeStr}`);
  return localDateTime.format(outFormat);
};

const capitalizeFirstLetter = (string) => {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : "";
};

const formatDate = (date, format = "DD MMM YYYY", defaultReturnValue = "-") => {
  return date ? moment(date).format(format) : defaultReturnValue;
};

export const formatDateUsingDayjs = (date, format = "DD MMM YYYY", defaultReturnValue = "-") => {
  return date ? dayjs(date).format(format) : defaultReturnValue;
};

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalizes a filter date value to YYYY-MM-DD using the local calendar (Date or parseable string).
 * Passes through values that are already YYYY-MM-DD. Avoids UTC midnight shifting the day in API payloads.
 */
export function formatAsYyyyMmDdLocalCalendar(value) {
  if (value == null) return null;
  if (typeof value === "string" && ISO_DATE_ONLY.test(value?.trim?.() || "")) {
    return value?.trim?.() || null;
  }
  const parsed = dayjs(value);
  return parsed?.isValid?.() ? parsed?.format?.("YYYY-MM-DD") : null;
}

/**
 * Returns a copy of box filter values with every daterange field serialized to YYYY-MM-DD for URL/API.
 */
export function serializeBoxFilterValuesForApply(filterValues, boxFilterConfig) {
  const next = { ...filterValues };
  (boxFilterConfig?.filters ?? []).forEach((filterDef) => {
    if (filterDef?.type !== "daterange") return;
    const range = next?.[filterDef?.id];
    if (!range) return;
    next[filterDef?.id] = {
      fromDate: formatAsYyyyMmDdLocalCalendar(range?.fromDate),
      toDate: formatAsYyyyMmDdLocalCalendar(range?.toDate),
    };
  });
  return next;
}

/** Display label for audit log `entity` values (API table names). */
export const getEntityTitle = (entity, auditLogPath) => {
  if (entity === "candidates" && auditLogPath === "employees") return "Employee";
  if (entity === "candidates") return "Candidate";
  if (entity === "positions") return "Position";
  if (entity === "touch_point_instances") return "Task";
  if (entity === "campaign_templates") return "Campaign Template";
  if (entity === "html_templates") return "Content Template";
  if (entity === "recruiter_scores") return "Recruiter Score";
  if (entity === "company_themes") return "Theme";
  return "Entity";
};

export const getLatestScore = (data) => {
  if (!data?.recruiterScores || data?.recruiterScores.length === 0) return null;

  return [...data.recruiterScores]  
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]; 
};


export const candidateStatusChangeWarningMessage = (updatedStatus) => 
  `Are you sure you want to change the status to "${updatedStatus}"? Once changed, status change cannot be reverted.`;

export const getExecutionDate = (task, tasksList) => {
  const format = "DD MMM YYYY";

  let exeDateTime;
  if (task?.executedTime) {
    exeDateTime = formatUtcTimeToIST(
      task?.executionDate,
      task.executedTime,
      "dddd, DD MMM YYYY hh:mm A"
    );
  } else {
    const exeTime = task?.touchPointStatus === "upcoming" ? "09:00 AM" : "";
    exeDateTime = dayjs(task?.executionDate).format("dddd, DD MMM YYYY ") + exeTime;
  }
  const candidateJoiningDate = getValueByPath(
    task,
    "CampaignInstance.Candidate.estimatedJoiningDate"
  );
  if (task.type !== "meeting") {
    return exeDateTime;
  }
  if (R.isNil(task.prevTaskExeDate) && R.isNil(task.nextTaskExeDate)) {
    return exeDateTime;
  }
  const startDate = task.prevTaskExeDate
    ? moment(task.prevTaskExeDate).add(1, "days").format(format)
    : formatDate(task.executionDate);
  const endDate = task.nextTaskExeDate
    ? moment(task.nextTaskExeDate).subtract(1, "days").format(format)
    : formatDate(
        candidateJoiningDate
          ? moment(candidateJoiningDate).subtract(1, "days").format(format)
          : task.executionDate
      );
  if (moment(startDate).isSameOrAfter(moment(endDate))) {
    return formatDate(exeDateTime, "dddd, DD MMM YYYY hh:mm A");
  }
  return `${startDate} - ${endDate}`;
};

const throwAPIError = ({ error }) => {
  if (error?.response.status === 401) {
    throw error.data.message;
  }
};

// Extracts and returns the plain text content from a HTML string
export const getPlainTextFromHTML = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const plainText = doc.body.innerText || doc.body.textContent;
  // Removing extra spaces and trimming the text
  return plainText.replace(/\s+/g, " ").trim();
}

export const checkFor401Error = (error) => {
  const { status } = error.response;
  return status === 401;
};

export const checkForTokenExpiredError = (error) => {
  const { status, data } = error.response;
  return status === 401 && data.message === "Token Expired!";
};

export const getDateDifference = ({ dateOne, dateTwo, diffIn = "days" }) => {
  return Math.max(moment(dateOne).diff(moment(dateTwo), diffIn), 0);
};

const getDateRange = (timeFrame) => {
  let set = { hour: 0, minute: 0, second: 0, millisecond: 0 };
  let format = "YYYY-MM-DD HH:mm:ss";
  return R.cond([
    [
      R.equals("lastWeek"),
      () => ({
        fromDate: moment().subtract(7, "days").set(set).format(format),
        toDate: moment().set(set).format(format),
      }),
    ],
    [
      R.equals("last15days"),
      () => ({
        fromDate: moment().subtract(15, "days").set(set).format(format),
        toDate: moment().set(set).format(format),
      }),
    ],
    [
      R.equals("lastMonth"),
      () => ({
        fromDate: moment().subtract(1, "month").set(set).format(format),
        toDate: moment().set(set).format(format),
      }),
    ],
    [
      R.equals("last90days"),
      () => ({
        fromDate: moment().subtract(90, "days").set(set).format(format),
        toDate: moment().set(set).format(format),
      }),
    ],
    [
      R.equals("lastSixMonths"),
      () => ({
        fromDate: moment()
          .subtract(5, "month")
          .startOf("month")
          .set(set)
          .format(format),
        toDate: moment().set(set).format(format),
      }),
    ],
    [
      R.equals("custom"),
      () => ({
        fromDate: moment(timeFrame.dates[0]).set(set).format(format),
        toDate: moment(timeFrame.dates[1]).set(set).format(format),
      }),
    ],
  ])(timeFrame.type);
};
export { capitalizeFirstLetter, formatDate, throwAPIError, getDateRange };

export const getWhatsAppBody = (type, newMeetingTemplate = false) => {
  if (type === "content") {
    return `Dear {{1}},
    We have something exciting for you to check out on our ANSR 1Connect platform, as we get closer to you joining us at {{2}}.
    
    To view {{3}}, sign in to your ANSR 1Connect account ({{4}}). Keep an eye out for further interesting information.
    
    Cheers to reading!
    Team {{5}}`;
  } else if (type === "meeting") {
    if (newMeetingTemplate) {
      return `Dear {{1}},

As your joining date approaches, we have a meeting set up for you with your {{2}} - {{3}} 
For further information, check your 1Connect account({{4}}) after logging in.

We're looking forward to meeting you!
Team {{5}}`;
    }
    return `Dear {{1}},
    We have a meeting set up for you with {{2}} on {{3}} as your joining date approaches.
    For further information, check your ANSR 1Connect account({{4}}) after logging in.
    
    We're looking forward to meeting you!
    Team {{5}}`;
  } else if (type === "give_away" || type == "survey") {
    return `Dear {{1}}
    We have a surprise for you on the way as your joining date approaches! For further information, check your ANSR 1Connect account ({{2}}) after logging in.
    
    Team {{3}}`;
  }
};

export const getUTCDateWithTime = (date, [h, m, s]) => {
  const mo = moment(date);
  mo.set("hour", h);
  mo.set("minute", m);
  mo.set("second", s);
  return mo.utc().toDate();
};

export const snakeToCamel = (str) => {
  return str.replace(/(?!^)_(.)/g, (_, char) => char.toUpperCase());
};

/**
 * Returns true if the filter value is empty / not applied (should not show a chip).
 */
export const isEmptyFilterValue = (value) => {
  if (R.isNil(value)) return true;
  if (R.isEmpty(value)) return true;
  if (
    R.is(Object, value) &&
    R.hasPath(["fromDate"], value) &&
    R.isNil(value.fromDate)
  ) {
    return true;
  }
  if (
    R.is(Object, value) &&
    R.hasPath(["min"], value) &&
    R.isNil(value.min) &&
    R.isNil(value.max)
  ) {
    return true;
  }
  return false;
};

/**
 * Returns only filter entries that have meaningful (applied) values, for chip display.
 */
export const getAppliedFiltersOnly = (selectedFilters) => {
  if (!selectedFilters || typeof selectedFilters !== "object") return {};
  const result = {};
  Object.keys(selectedFilters).forEach((key) => {
    if (key === "customAttributeFilters") {
      const custom = selectedFilters[key] || {};
      const customResult = {};
      Object.keys(custom).forEach((ck) => {
        if (!isEmptyFilterValue(custom[ck]?.value)) {
          customResult[ck] = custom[ck];
        }
      });
      if (!R.isEmpty(customResult)) result[key] = customResult;
    } else {
      if (!isEmptyFilterValue(selectedFilters[key]?.value)) {
        result[key] = selectedFilters[key];
      }
    }
  });
  return result;
};

export const removeNotSelectedFilterData = (filters) => {
  Object.keys(filters).forEach((key) => {
    if (R.isNil(filters[key].value) || R.isEmpty(filters[key].value)) {
      delete filters[key];
    } else if (
      R.is(Object, filters[key].value) &&
      R.hasPath(["fromDate"], filters[key].value) &&
      (R.isNil(filters[key].value.fromDate) || R.isNil(filters[key].value.toDate))
    ) {
      delete filters[key];
    } else if (
      R.is(Object, filters[key].value) &&
      R.hasPath(["min"], filters[key].value) &&
      R.isNil(filters[key].value.min) &&
      R.isNil(filters[key].value.max)
    ) {
      delete filters[key];
    }
  });
  return filters;
};

export const getSelectedFilters = (filters) => {
  const newFilters = { ...filters };
  const customFilters = newFilters.customAttributeFilters || {};
  const selectedFilterData = {
    ...removeNotSelectedFilterData(newFilters),
    customAttributeFilters: {
      ...removeNotSelectedFilterData(customFilters),
    },
  };
  if (R.isEmpty(selectedFilterData.customAttributeFilters)) {
    delete selectedFilterData["customAttributeFilters"];
  }
  return selectedFilterData;
};

export const getInitialFilterData = (preSelectedFilters) => {
  return {
    timeframe: {
      label: "Time Frame",
      type: "date",
      value: {},
    },
    touchPointType: {
      label: "Touchpoint Type",
      type: "single select",
      value: preSelectedFilters?.touchPointType,
    },
    recruiters: {
      label: "Recruiters",
      type: "multi select",
      value: preSelectedFilters.recruiters ? preSelectedFilters.recruiters : [],
    },
    hiringManagers: {
      label: "Hiring Managers",
      type: "multi select",
      value: preSelectedFilters.hiringManagers
        ? preSelectedFilters.hiringManagers
        : [],
    },
    campaignTemplates: {
      label: "Journeys",
      type: "multi select",
      value: preSelectedFilters.campaignTemplates
        ? preSelectedFilters.campaignTemplates
        : [],
    },
    joiningDate: {
      label: "Joining Date",
      type: "date",
      value: {
        fromDate: null,
        toDate: null,
      },
    },
    executionDate: {
      label: "Execution Date",
      type: "date",
      value: {
        fromDate: null,
        toDate: null,
      },
    },
    experience: {
      label: "Experience",
      type: "integer",
      value: preSelectedFilters.experience
        ? preSelectedFilters.experience
        : null,
    },
    status: {
      label: "Status",
      type: "single select",
      value: preSelectedFilters?.status,
    },
    engagementLevel: {
      label: "Engagement Level",
      type: "single select",
      value: preSelectedFilters?.engagementLevel,
    },
    taskErrorType: {
      label: "Failed Type",
      type: "single select",
      value: preSelectedFilters?.taskErrorType,
    },
    failedTaskStatus: {
      label: "Status",
      type: "single select",
      value: preSelectedFilters?.failedTaskStatus,
    },
    userRole: { label: "Roles", value: "", type: "single select" },
    company: { label: "Company", value: null, type: "single select" },
    country: { label: "Country", value: null, type: "single select" },
    companyAdmins: { label: "Company Admin", value: [], type: "multi select" },
    meetingStatus: {
      label: "Meeting Status",
      type: "single select",
      value: preSelectedFilters?.meetingStatus,
    },
  };
};

export const getBody = (touchPoint) => {
  return touchPoint.bodyType === "html_template" && touchPoint.bodyHtmlTemplate
    ? touchPoint.bodyHtmlTemplate.html
    : touchPoint.body;
};

export const getBodyFromHtmlTemplate = (touchPoint, htmlTemplates) => {
  if (touchPoint?.bodyType === "html_template") {
    const found = htmlTemplates.find(
      (htmlTemplate) => htmlTemplate.value == touchPoint.bodyHtmlTemplateId
    );
    return found ? found.html : touchPoint.body;
  } else {
    return touchPoint.body;
  }
};

export const roleDefaultPage = (roles, company) => {
  const baseUrl = "/[company]/admin";
  if (isCustomerSuccess(roles)) {
    return {
      pathname: `${baseUrl}/dashboard`,
      query: { company: company.slug },
    };
  }
  if (
    roles?.find((role) =>
      [
        "Leadership",
        "Company Admin",
        "Recruiter Manager",
        "Recruiter",
        "Talent Acquisition Head",
        "HR Ops",
        "TA Director"
      ].includes(role.name)
    )
  ) {
    return {
      pathname: `${baseUrl}/dashboard`,
      query: { company: company.slug },
    };
  }
  if (roles?.find((role) => role.name === "Hiring Manager")) {
    return {
      pathname: `${baseUrl}/positions`,
      query: { company: company.slug },
    };
  }
  if (roles?.find((role) => role.name === "Campaign Admin")) {
    return {
      pathname: `${baseUrl}/journey-settings/my-journey`,
      query: { company: company.slug },
    };
  }
  if (roles?.find((role) => role.name === "Newsfeed Admin")) {
    return {
      pathname: `${baseUrl}/news-feeds`,
      query: { company: company.slug },
    };
  }
  if (roles?.find((role) => role.name === "Report Admin")) {
    return {
      pathname: `${baseUrl}/dashboard`,
      query: { company: company.slug },
    };
  }
  if (roles?.find((role) => role.name === ROLES.SITE_LEADER)) {
    return false;
  }
};

export const platformUsers = [
  ROLES.SUPER_ADMIN,
  ROLES.CUSTOMER_SUCCESS,
  ROLES.SUPER_ADMIN_CAMPAIGN_MANAGER,
];

export const getRoleName = (role) =>
  typeof role === "string" ? role : role?.name || role?.label;

export const getPlatformUserRole = (roles = []) =>
  (roles || []).map(getRoleName).find((roleName) => platformUsers.includes(roleName));

export const isPlatformUser = (roles = []) => Boolean(getPlatformUserRole(roles));

export const isCustomerSuccess = (roles = []) =>
  getPlatformUserRole(roles) === ROLES.CUSTOMER_SUCCESS;

export const hasSuperAdminOrSuperAdminCampaignManagerRole = (roles = []) =>
  [ROLES.SUPER_ADMIN, ROLES.SUPER_ADMIN_CAMPAIGN_MANAGER].includes(
    getPlatformUserRole(roles)
  );

export const getCustomFilterObjects = (
  config,
  selectedCustomFilters,
  handleCustomAttributeChange
) => {
  const type = config.type;
  switch (type) {
    case "text":
      return {
        type: "text",
        isFilterSelected:
          !!selectedCustomFilters[config.id].value &&
          selectedCustomFilters[config.id].value !== "",
        options: getCustomFilterOptions(
          type,
          config,
          selectedCustomFilters,
          handleCustomAttributeChange
        ),
      };
    case "integer":
    case "double":
      return {
        type: "radio",
        isFilterSelected:
          !!selectedCustomFilters[config.id].value &&
          selectedCustomFilters[config.id].value?.type !== "all",
        options: getCustomFilterOptions(
          type,
          config,
          selectedCustomFilters,
          handleCustomAttributeChange
        ),
      };
    case "single select":
    case "multi select":
      return {
        type: "checkbox",
        isFilterSelected:
          selectedCustomFilters[config.id].value &&
          selectedCustomFilters[config.id].value.length > 0,
        show: config.options.length > 1,
        options: getCustomFilterOptions(
          type,
          config,
          selectedCustomFilters,
          handleCustomAttributeChange
        ),
      };
    case "date":
      return {
        type: "daterange",
        isFilterSelected:
          selectedCustomFilters[config.id].value &&
          !!(
            selectedCustomFilters[config.id].value.fromDate &&
            selectedCustomFilters[config.id].value.toDate
          ),
        dateRangeProps: getCustomFilterOptions(
          type,
          config,
          selectedCustomFilters,
          handleCustomAttributeChange
        ),
      };
    case "boolean":
      return {
        type: "radio",
        isFilterSelected: !!selectedCustomFilters[config.id].value,
        options: getCustomFilterOptions(
          type,
          config,
          selectedCustomFilters,
          handleCustomAttributeChange
        ),
      };
  }
};
const getCustomFilterOptions = (
  type,
  config,
  selectedCustomFilters,
  handleCustomAttributeChange
) => {
  switch (type) {
    case "text":
      return [
        {
          id: config.id,
          name: config.name,
          value: selectedCustomFilters[config.id].value,
          isSelected: !!selectedCustomFilters[config.id].value,
          custom: true,
          handleChange: (e) => {
            handleCustomAttributeChange(config, e);
          },
        },
      ];
    case "integer":
    case "double":
      const labels = [
      "All",
      "Greater Than",
      "Lesser Than",
      "In Between",
      "Equal to",
    ];
    let options = [
      "all",
      "greaterThan",
      "lesserThan",
      "inBetween",
      "equalTo",
    ];
    if (type === "double") {
      options = options.filter((option) => option !== "equalTo");
    }
    return options.map((option, index) => ({
        id: option,
        name: option,
        value: option,
        isSelected: selectedCustomFilters[config.id].value?.type === option,
        handleChange: (e) => handleCustomAttributeChange(config, e),
        label: labels[index],
        custom: option !== "all",
        customConfig:
          option === "all"
            ? {}
            : option !== "inBetween"
            ? {
                inputType: "number",
                placeholder: "Enter Number",
                type: "textField",
                value:
                  option === "greaterThan" || option === "equalTo"
                    ? selectedCustomFilters[config.id].value?.min
                    : selectedCustomFilters[config.id].value?.max,
                onChange: (e) => {
                  handleCustomAttributeChange(config, e, option);
                },
              }
            : {
                minPlaceHolder: "Start",
                maxPlaceHolder: "End",
                inputType: "number",
                type: "rangeTextField",
                min: selectedCustomFilters[config.id].value?.min,
                max: selectedCustomFilters[config.id].value?.max,
                onChange: (e) => handleCustomAttributeChange(config, e, option),
              },
      }));

    case "single select":
    case "multi select":
      return config.options.map((option) => ({
        id: option.value,
        name: config.id,
        value: option.value,
        isSelected:
          selectedCustomFilters[config.id].value &&
          selectedCustomFilters[config.id].value.includes(option.value),
        handleChange: (e) => handleCustomAttributeChange(config, e),
        label: option.value,
      }));
    case "date":
      return {
        onDateChange: (e) => handleCustomAttributeChange(config, e),
        dateRange: [
          selectedCustomFilters[config.id].value.fromDate,
          selectedCustomFilters[config.id].value.toDate,
        ],
      };
    case "boolean":
      return ["true", "false"].map((option) => ({
        id: option,
        name: option,
        value: option,
        isSelected: selectedCustomFilters[config.id].value === option,
        handleChange: (e) => handleCustomAttributeChange(config, e),
        label: option === "true" ? "Yes" : "No",
      }));
  }
};

export const removeUndefinedValues = (data) => {
  const dataWithOutUndefined = {};
  Object.keys(data).forEach((key) => {
    if (data[key] !== undefined) {
      dataWithOutUndefined[key] = data[key];
    }
  });
  return dataWithOutUndefined;
};

export const getCompanyDetails = async (companyId) => {
  const response = await getCompany(companyId);
  if(response.status) {
    return response.entity;
  }
  return {};
};

export const removeCustomAttributeEmptyValues = (data) => {
  const dataWithOutEmptyValues = {};
  data &&
    Object.keys(data).forEach((key) => {
      if (data[key] !== undefined && data[key] !== "" && data[key] !== null) {
        dataWithOutEmptyValues[key] = data[key];
      }
    });
  return dataWithOutEmptyValues;
};

export const computeAndSetPrimaryShades = async ({ document, color }) => {
  const primary50 = chroma(color).brighten(0.8).hex();
  const primary100 = chroma(color).brighten(0.6).hex();
  const primary300 = chroma(color).brighten(0.4).hex();
  const primary400 = chroma(color).brighten(0.2).hex();
  const primary500 = chroma(color).hex();
  const primary600 = chroma(color).darken(0.2).hex();
  const primary700 = chroma(color).darken(0.6).hex();
  const primary900 = chroma(color).darken(1).hex();
  document.documentElement.style.setProperty("--primary-color-50", primary50);
  document.documentElement.style.setProperty("--primary-color-100", primary100);
  document.documentElement.style.setProperty("--primary-color-300", primary300);
  document.documentElement.style.setProperty("--primary-color-400", primary400);
  document.documentElement.style.setProperty("--primary-color-500", primary500);
  document.documentElement.style.setProperty("--primary-color-600", primary600);
  document.documentElement.style.setProperty("--primary-color-700", primary700);
  document.documentElement.style.setProperty("--primary-color-900", primary900);
};

export const getEmojiSrc = (circleValue) => {
  if (circleValue === "disengaged") return "/images/icons/sad-emoji-ac.svg";
  if (circleValue === "low" || circleValue === "negative")
    return "/images/icons/negative.svg";
  if (circleValue === "medium" || circleValue === "neutral")
    return "/images/icons/neutral.svg";
  if (circleValue === "high" || circleValue === "positive")
    return "/images/icons/positive.svg";
  if (circleValue === null)
    return "/images/icons/neutral.svg";
  else {
    if (circleValue > 67) return "/images/icons/love-emoji-ac.svg";
    if (circleValue > 33 && circleValue <= 66)
      return "/images/icons/happy-emoji-ac.svg";
    return "/images/icons/sad-emoji-ac.svg";
  }
};
export const getEmojiText = (circleValue) => {
  if (Number.isInteger(Number(circleValue)) || circleValue === "disengaged") {
    if (circleValue > 67) return "Positive";
    if (circleValue > 33 && circleValue <= 66) return "Neutral";
    return "Negative";
  }
  if (circleValue === "low") return "Negative";
  if (circleValue === "medium") return "Neutral";
  if (circleValue === "high") return "Positive";
  return circleValue;
};

export const getHideScrollBarCss = () => {
  return `
    .u_body {
        -ms-overflow-style: none;  /* Internet Explorer 10+ */
        scrollbar-width: none;  /* Firefox */
    }
    .u_body::-webkit-scrollbar { 
        display: none;  /* Safari and Chrome */
    }
  `;
};
export const getCustomFedexCss = (companyId) => {
  const originUrl = window.location.origin;
  const bestBuyId = process.env.NEXT_PUBLIC_BEST_BUY_ID;
  const dthId = process.env.NEXT_PUBLIC_DTH_ID;
  if (
    companyId === "e7a809d1-79ba-4c05-a82a-da1446f5e259" ||
    companyId === "df0f4079-94d9-4467-9e0e-55d8f2b8a82b"
  ) {
    return `
    ${getHideScrollBarCss()}
    @font-face {
        font-family: 'FedEx Sans';
        src: url('${originUrl}/fonts/FedExSans_Rg.ttf') format('truetype');
      }
      @font-face {
        font-family: 'FedEx Sans Light';
        src: url('${originUrl}/fonts/FedExSans_lt.ttf') format('truetype');
      }
      * {
        font-family: 'FedEx Sans', sans-serif !important;
      }
      h1, h2, h3, h4 {
        font-family: 'FedEx Sans Light', sans-serif !important;
      }`;
  }

  if (companyId === "dd58e4cc-44a6-4caa-b302-363e15ba9a8c") {
    return `
    ${getHideScrollBarCss()}
    @font-face {
        font-family: 'DS Indigo';
        src: url('${originUrl}/fonts/dsindigo-regular.woff2') format('woff2') , url('${originUrl}/fonts/dsindigo-regular.woff') format('woff');
      }

      * {
        font-family: 'DS Indigo', sans-serif !important;
      }
    `;
  }

  if (companyId === bestBuyId) {
    return `
    ${getHideScrollBarCss()}
    @font-face {
        font-family: 'HumanBBYOffice';
        src: url('${originUrl}/fonts/HumanBBYOffice_Regular.ttf') format('truetype');
      }

      * {
        font-family: 'HumanBBYOffice', sans-serif !important;
      }
    `;
  }

  if (companyId === dthId) {
    return `
    ${getHideScrollBarCss()}
    @font-face {
        font-family: 'Verdana';
        src: url('${originUrl}/fonts/verdana.ttf') format('truetype');
      }

      * {
        font-family: 'Verdana', sans-serif !important;
      }
    `;
  }
  
  return "";
};

export const isValidToRun = (task) => {
  let invalidTokens = [
    "CANDIDATE_NAME",
    "POSITION_TITLE",
    "DOJ",
    "COMPANY_NAME",
    "RECRUITER_NAME",
    "HIRING_MANAGER_NAME",
    "COMPANY_ADDRESS",
    "CANDIDATE_ADDRESS",
    "TOUCHPOINT_TITLE",
  ];
  if (task.type === "meeting") {
    invalidTokens = [
      ...invalidTokens,
      "MEETING_TYPE",
      "MEETING_PERSON",
      "MEETING_LINK",
      "MEETING_LOCATION",
      "MEETING_DATE",
    ];
  }
  const validPlaceHolders = task.placeholders.filter(
    (field) => !invalidTokens.includes(field)
  );
  const isValid = validPlaceHolders.reduce((accumulator, currentValue) => {
    return (
      accumulator && task[snakeToCamel(currentValue.toLowerCase())] !== null
    );
  }, true);
  return isValid;
};

export const computeThemeColors = async (company) => {
  const { theme } = company;
  if (theme?.headerType === "custom") {
    const headerColor = theme.headerColor;
    document.documentElement.style.setProperty("--header", headerColor);
  }
  if (theme?.taskCardType === "custom") {
    if (theme?.taskCardColor.includes("linear-gradient")) {
      document.documentElement.style.setProperty(
        "--stage-overlay",
        theme.taskCardColor
      );
    } else {
      document.documentElement.style.setProperty(
        "--stage-solid",
        theme.taskCardColor
      );
    }
  }
  if (theme?.openTaskCardTextType === "custom" && theme?.openTaskCardTextColor) {
    document.documentElement.style.setProperty(
      "--open-task-card-text",
      theme.openTaskCardTextColor
    );
  }
  if (theme?.lockedTaskCardType === "custom" && theme?.lockedTaskCardColor) {
    document.documentElement.style.setProperty(
      "--locked-task-card-bg",
      theme.lockedTaskCardColor
    );
  }
  if (theme?.lockedTaskCardTextType === "custom" && theme?.lockedTaskCardTextColor) {
    document.documentElement.style.setProperty(
      "--locked-task-card-text",
      theme.lockedTaskCardTextColor
    );
  }
};

export const getURL = (endpoint) => {
  if (process.env.NODE_ENV === "development") {
    return `http://localhost:4200/${endpoint}`;
  }
  if (process.env.NODE_ENV === "preprod") {
    return `https://uat.1connect.ansr.com/${endpoint}`;
  }
  if (process.env.NODE_ENV === "sandbox") {
    return `https://sandbox.1connect.ansr.com/${endpoint}`;
  }
  if (process.env.NODE_ENV === "prod") {
    return `https://1connect.ansr.com/${endpoint}`;
  }
  if (process.env.NODE_ENV === "automation") {
    return `https://automation.canengage.ansr.com/${endpoint}`;
  }
  return `http://localhost:4200/${endpoint}`;
};

export const getValueByPath = (obj, path, defaultReturn = null) => {
  const properties = path.split(".");
  let value = obj;

  for (let prop of properties) {
    if (value && value.hasOwnProperty(prop)) {
      value = value[prop];
    } else {
      // Path does not exist, return null
      return defaultReturn;
    }
  }
  // Return the value at the path
  return value;
};

export const getFeatureFlags = () => {
  let ansrFlags = {};
  let enabledFlagsArr = [];
  if (
    process.env.NODE_ENV === "preprod" ||
    process.env.NODE_ENV === "sandbox"
  ) {
    enabledFlagsArr = [
      "ai_assist",
      "hide_need_help",
      "new_meeting_gupshup_template",
      "onsite_meeting",
      "recruiter_intervention_delete",
      "recruiter_intervention_edit_view",
    ];
  } else {
    enabledFlagsArr = [
      "ai_assist",
      "hide_need_help",
      "onsite_meeting",
      "recruiter_intervention_delete",
      "recruiter_intervention_edit_view",
    ];
  }
  enabledFlagsArr.forEach((val) => {
    ansrFlags[val] = {
      enabled: true,
    };
  });
  return ansrFlags;
};

export const isRecruiterSentimentReadOnly = ({estimatedJoiningDate, status}) => {
  let warnings = ''

const currDate = dayjs();
const joinDate = dayjs(estimatedJoiningDate);

  if(status !== "Offer Accepted"){
    warnings += 'Interventions can be added only for candidates in Offer Accepted stage'
  }

  if( joinDate.isSameOrBefore(currDate) && status === "Offer Accepted"){
    warnings += 'Interventions Window Is Now Closed. Intervention can only be added prior to the DOJ'
  }

  return {
    status: warnings.length > 0,
    warnings
  }
}



export const formatMillisecondsToMinutesSeconds = (ms) => `${Math.floor(ms / 60000)} min ${Math.floor((ms % 60000) / 1000)} sec`;

export const openWhatsAppAndSendMessage = (message, fromNumber) => {
  const whatsappUrl = `${process.env.NEXT_PUBLIC_WHATS_APP_URL}${fromNumber}?text=${message}`;
  window.open(whatsappUrl, '_blank');
}

export const formatRecruiterScoreData = (data) => {
  if (!data) return [];
  return data.map(item => {
      return {
          circleValue: item.recruiterScore,
          date: dayjs(item.createdAt).format("DD MMM YYYY"),
          recruiterName: item?.recruiter?.name || item?.recruiterName,
          description: item.additionalComments,
          ...item
      }
  })
}

export const formatInterventionSummaryItem = (item) => ({
  circleValue: item.recruiterScore,
  date: dayjs(item.createdAt).format("DD MMM YYYY"),
  recruiterName: item.recruiter?.name,
  description: item.additionalComments,
  ...item,
});

export const getExtractNumber = (input) => {
  if (
    typeof input === "string" &&
    input != undefined &&
    (input.includes("+") || input.includes("-"))
  ) {
    const sanitizedInput = input.replace(/[+-]/g, '').trim(); // Remove all '+' and '-' symbols and trim whitespace
    return parseInt(sanitizedInput);
  }
  return parseInt(input);
};

export const getEntityFromResponse = (response) => {
  if(response.status) {
    return response.entity;
  }
  return {};
}

export const surveyPersonFromCampaignInstance = (campaignInstance) =>
  campaignInstance?.Candidate || campaignInstance?.employee || null;

/** First two cells for survey export rows: display name, work/personal email. */
export const buildSurveyReportPersonColumns = (campaignInstance) => {
  const person = surveyPersonFromCampaignInstance(campaignInstance);
  const email = person?.officialEmailId || person?.emailId || "";
  return [person?.name || "no_name", email];
};

export const checkIfFilesAreTooBig = (files) => {
  let valid = true
  if (files) {
    files.map(file => {
      const size = file.size / 1024 / 1024
      if (size > fileSizes.image) {
        valid = false
      }
    })
  }
  return valid
}

export const parseData = (file) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const wb = read(arrayBuffer, { type: "buffer" });
      const wsName = wb.SheetNames[0];
      const ws = wb.Sheets[wsName];
      const data = utils.sheet_to_json(ws);
      resolve(data);
    };
    reader.onerror = (err) => {
      reject(err);
    };
  });
};

export const getAllCampaignsTemplates = async (offset, search, sort, filters) => {
  const companyId = getCompanyId();
  const response = await getCampaignTemplates(
    companyId,
    offset,
    search,
    sort,
    filters
  );
  if (response.status) {
    return response.entity;
  }

  return {
    rows: [],
    count: 0,
  };
};

export const extractAndExcludeCurlyBrackets = (html) => {
  const results = [];
  const regex = /{+[^{}]*}+/g;

  html.replace(regex, (match) => {
    if (!/[^{}]*:\s*[^{}]*;?/.test(match)) {
      results.push(match);
    }
  });

  return results;
};

export const getMandatoryPlaceholdersData = (touchPointInstance, company) => ({
        CANDIDATE_NAME: touchPointInstance?.CampaignInstance?.Candidate?.name,
        POSITION_TITLE: touchPointInstance?.CampaignInstance?.Candidate?.position?.title,
        DOJ: dayjs(touchPointInstance?.CampaignInstance?.Candidate?.estimatedJoiningDate)?.format('DD MMM YYYY'),
        COMPANY_NAME: company?.companyName,
        RECRUITER_NAME: touchPointInstance?.CampaignInstance?.Candidate?.position?.recruiterManager?.name,
        HIRING_MANAGER_NAME: touchPointInstance?.CampaignInstance?.Candidate?.position?.hiringManager?.name,
        CANDIDATE_ADDRESS: touchPointInstance?.CampaignInstance?.Candidate?.address,
        TOUCHPOINT_TITLE: touchPointInstance?.title,
        CANDIDATE_NUMBER: touchPointInstance?.CampaignInstance?.Candidate?.mobileNumber || 'not provided',
        CANDIDATE_EMAIL: touchPointInstance?.CampaignInstance?.Candidate?.emailId,
        MEETING_TYPE: touchPointInstance.meetingType,
        MEETING_PERSON: touchPointInstance.meetingPerson,
        MEETING_LINK: touchPointInstance.meetingLink,
        MEETING_LOCATION: touchPointInstance.meetingLocation,
        MEETING_DATE: touchPointInstance.meetingDate && getDateInCompanyTimeZone(touchPointInstance.meetingDate) || "",
        GOOGLE_MAPS_LINK: touchPointInstance.googleMapsLink,
        DISPATCH_DATE: touchPointInstance.dispatchDate,
        DISPATCH_ADDRESS: touchPointInstance.dispatchAddress,
        POINT_OF_CONTACT: touchPointInstance.pointOfContact
});

export const getTimezoneAbbreviation = (timezone) => {
  const now = moment.tz(timezone);
  return now.zoneAbbr();
};

export const getCountriesListWithTimezones = () => {

  const countryList = timezones.map((timezone) => ({
    country: timezone.locations,
    timezone: timezone.timezone,
    abbreviation: timezone.code,
  }));

  const timezoneList = countryList.map((timezone) => ({
    value: timezone.timezone,
    label: `${timezone.country} - ${timezone.timezone} (${timezone.abbreviation})`,
    timeZone: timezone.timezone,
  }));

  return timezoneList;
}

export const isSuperAdminCampaignManager = (userDetails) => {
  return getPlatformUserRole(userDetails?.roles) === ROLES.SUPER_ADMIN_CAMPAIGN_MANAGER;
};

export const isCurrentUserSuperAdmin = (userDetails) => {
  return getPlatformUserRole(userDetails?.roles) === ROLES.SUPER_ADMIN;
};

export const loggedInUserHasRequiredRole = (currentUser, allowedRoles) => {
  if (!currentUser || !allowedRoles || !Array.isArray(allowedRoles)) {
    return false;
  }

  const companyId = getCompanyId();
  if (!companyId || companyId === "null") {
    return false;
  }

  // Find the current company from user's companies
  const userCompanies = currentUser?.company || [];
  const currentCompany = userCompanies?.find(
    (company) => company?.id === companyId
  );

  if (!currentCompany) {
    return false;
  }

  // Extract all role names for this company
  const userRoles = currentCompany?.userRoles || [];
  const companyRoleNames = userRoles
    ?.map((roleObj) => roleObj?.Role?.name || roleObj?.name)
    ?.filter(Boolean);
  const platformSessionRoleNames = (currentUser?.roles || [])
    ?.map((roleObj) => getRoleName(roleObj))
    ?.filter((roleName) => platformRolesDisassociatedFromCompany.includes(roleName))
    ?.filter(Boolean);
  const roleNames = [
    ...new Set([
      ...(companyRoleNames || []),
      ...(platformSessionRoleNames || []),
    ]),
  ];
  
  // Check if user has any of the allowed roles
  return roleNames?.some((role) => allowedRoles?.includes(role)) || false;
};

export const getEngagementLevel = (score) => {
  if (score >= engagementScore.hotMin) return "hot";
  if (score >= engagementScore.warmMin && score < engagementScore.hotMin) return "warm";
  if (score < engagementScore.warmMin) return "cold";
};

export const getBtnColor = (theme, isPreview = false) => {
  if (!theme) {
    return {
      notAvailable: "text-secondary-10 bg-white-500 border-secondary-10",
      save: "bg-secondary-10 bg-stage-overlay",
      datePicker: "black",
    };
  }

  if (theme.taskCardType === "custom") {
    if (theme.taskCardColor.includes("linear-gradient")) {
      return {
        notAvailable: isPreview
          ? "text-preview-stage-overlay border-preview-stage-overlay"
          : "text-stage-overlay border-stage-overlay",
        save: isPreview ? "bg-preview-stage-overlay" : "bg-stage-overlay",
        datePicker: isPreview ? "var(--preview-stage-overlay)" : "black",
      };
    } else {
      document.documentElement.style.setProperty(
        "--stage-solid",
        theme.taskCardColor
      );
      return {
        notAvailable: isPreview
          ? "text-primary-preview-task border-primary-preview-task bg-white"
          : "border text-primary-task border-current bg-white",
        save: isPreview ? "bg-primary-preview-task" : "bg-primary-task",
        datePicker: isPreview ? "var(--primary-preview-task)" : "var(--stage-solid)",
      };
    }
  }

  return {
    notAvailable: "text-[#005465] bg-white-500 border-[#005465]",
    save: "bg-secondary-10 bg-stage-overlay",
    datePicker: "#005465",
  };
};

export const formatDate2 = (inputDate) => {
 return dayjs(inputDate).format("D MMM YYYY");
}


// Getting the time to read a string
export const getReadTime = (body) => {
  const words = body.trim().split(/\s+/);
  return (words.length / wpm);
};


// Getting the time to read a content based on the number of words
export const getReadTimeWithNumberOfWords = (noOfWords) => (noOfWords / wpm);


export const modifyCanvasCompanyAllowed = (privileges, company) => {
  return company.existInCanvas
    ? hasPrivilege(privileges, {
        [PRIVILEGE_SLUG.modifyCanvasIntegratedCompany]: [
          PRIVILEGE_ACTION.UPDATE,
          PRIVILEGE_ACTION.CREATE,
        ],
      })
    : true;
};

export const getButtonThemeColor = (theme, isPreview) => {
  if (!theme) {
    return "bg-secondary-10 bg-stage-overlay";
  }
  if (theme.taskCardType === "custom") {
    if (theme.taskCardColor.includes("linear-gradient")) {
      return isPreview ? "bg-preview-stage-overlay" : "bg-stage-overlay";
    } else {
      return isPreview ? "bg-primary-preview-task" : "bg-primary-task";
    }
  }
  return "bg-secondary-10 bg-stage-overlay";
};

export const getArrowColor = (
  theme,
  isPreview,
  isSuperAdmin,
  StageSelected
) => {
  if (isSuperAdmin) {
    return StageSelected ? "" : "text-primary-600";
  }
  if (!theme) {
    return "text-black";
  }
  if (theme.taskCardType === "custom") {
    if (theme.taskCardColor.includes("linear-gradient")) {
      return `${
        isPreview ? "text-preview-stage-overlay" : "text-stage-overlay"
      }`;
    } else {
      return isPreview ? "text-primary-task" : "text-primary-task";
    }
  }
  // 1Connect default (or unset taskCardType)
  return "text-black";
};

export const getOpenTaskCardTextClass = (theme, isPreview, StageSelected) => {
  if (theme?.openTaskCardTextType === "custom" && theme?.openTaskCardTextColor) {
    return isPreview
      ? "text-primary-preview-open-task-text"
      : "text-primary-open-task-text";
  }
  return StageSelected ? "text-lightGrey" : "text-gray-900";
};

export const getLockedTaskCardTextClass = (theme, isPreview) => {
  if (theme?.lockedTaskCardTextType === "custom" && theme?.lockedTaskCardTextColor) {
    return isPreview
      ? "text-primary-preview-locked-task-text"
      : "text-primary-locked-task-text";
  }
  return "text-gray-900";
};

export const getLockedTaskCardBgStyle = (theme) => {
  if (theme?.lockedTaskCardType !== "custom" || !theme?.lockedTaskCardColor) {
    return undefined;
  }
  return { background: theme.lockedTaskCardColor };
};

export const getLockedTaskCardBgClass = (theme) => {
  if (theme?.lockedTaskCardType === "custom" && theme?.lockedTaskCardColor) {
    return "";
  }
  return "bg-primary-task-disabled";
};

export const getBorder = (score) => {
  if (score >= 80) return "#F44336";
  if (score >= 60) return "#FF6D00";
  if (score >= 40) return "#FFF176";
  if (score >= 20) return "#12AB0F";
  return '#4FBAF0'
}

export const isValidPhoneNumber = (phoneNumber) => {
  return /^\+?[0-9]{10,14}$/.test(phoneNumber);
};

export const getTextAndColor = (score) => {
  if (score < engagementScore?.warmMin) return { color: "text-info", text: "Cold" };
  if (score < engagementScore?.hotMin) return { color: "text-warning", text: "Warm" };
  return { color: "text-error", text: "Hot" };
};

export const getTaskCardColor = ({
  isSuperAdmin,
  StageSelected,
  journey,
  theme,
  isPreview,
}) => {
  const taskCardType = theme?.theme?.taskCardType || theme?.taskCardType;
  const taskCardColor = theme?.taskCardColor;

  if (isSuperAdmin) {
    return StageSelected ? "bg-primary-600" : "bg-[#F8F8F8]";
  }

  if (journey) {
    if (taskCardType === "default") {
      return StageSelected ? "bg-primary-600" : "bg-[#F8F8F8]";
    }
    if (taskCardType === "custom") {
      return StageSelected ? "bg-primary-task" : "bg-[#F8F8F8]";
    }
  }

  if (!theme) {
    return "bg-secondary-10 bg-stage-overlay";
  }

  if (!StageSelected) {
    return "bg-primary-task-disabled";
  }

  if (taskCardType === "custom") {
    if (taskCardColor?.includes("linear-gradient")) {
      return `bg-secondary-10 ${isPreview ? "bg-preview-stage-overlay" : "bg-stage-overlay"}`;
    }
    return isPreview ? "bg-primary-preview-task" : "bg-primary-task";
  }

  return "bg-secondary-10 bg-stage-overlay";
};

export const getMappedPositions = async (search, offset = 0) => {
  const companyId = getCompanyId();

  const response = await getPositions(
    companyId,
    search,
    offset,
    10,
    undefined,
    undefined
  );

  const allPositions = response?.entity?.rows || [];
  return allPositions.filter((pos) => pos.status !== "Open");
};

export const getNextCallDate = (callDetails) => {
  const today = startOfDay(new Date());
  const lastCall = callDetails?.[0]?.createdAt;
  
  if (isMonday(today)) {
    const hadCallToday = callDetails?.some(call => {
      const callDate = startOfDay(new Date(call.createdAt));
      return callDate.getTime() === today.getTime();
    });
    if (!hadCallToday) {
      return today;
    }
  }

  if (lastCall) {
    const lastCallDate = startOfDay(new Date(lastCall));
    const nextMondayAfterLastCall = nextMonday(lastCallDate);
    
    if (isBefore(nextMondayAfterLastCall, today)) {
      return nextMondayAfterLastCall;
    }
    
    return nextMondayAfterLastCall;
  }

  return nextMonday(today);
};

export const validateNoOverlap = (date, from, to, selectedTimes) => {
  // Filters the selected times for the same day that have at least one time range
  const conflictingTimes = selectedTimes.filter(
    (item) =>
      item.date === moment(date).format("YYYY-MM-DD") && item.times.length > 0
  );

  // Iterate over each conflicting time range and check for overlaps
  for (const item of conflictingTimes) {
    for (const { startTime, endTime } of item.times) {
      const start = moment(moment(startTime).format("hh:mm A"), "hh:mm A");
      const end = moment(moment(endTime).format("hh:mm A"), "hh:mm A");

      const selectedStart = moment(from, "hh:mm A");
      const selectedEnd = moment(to, "hh:mm A");

      // Check if the selectedStart lies within an existing slot
      if (selectedStart.isBetween(start, end, null, "[)")) {
        return false;
      }

      // Check if the selectedEnd lies within an existing slot
      if (selectedEnd.isBetween(start, end, null, "(]")) {
        return false;
      }

      // Check if a slot starts within the selected range
      if (start.isBetween(selectedStart, selectedEnd, null, "[)")) {
        return false;
      }

      // Check if a slot ends within the selected range
      if (end.isBetween(selectedStart, selectedEnd, null, "(]")) {
        return false;
      }
    }
  }

  return true; // No overlap detected
};

export const mapSlotsByDate = (slots) => {
  if (!Array.isArray(slots)) return [];
  return slots.reduce((acc, slot) => {
    const dateStr = moment(slot.date).format("YYYY-MM-DD");
    const existingDate = acc.find((item) => item.date === dateStr);
    const timeRange = { startTime: slot.startTime, endTime: slot.endTime };
    if (existingDate) {
      existingDate.times.push(timeRange);
    } else {
      acc.push({ date: dateStr, times: [timeRange] });
    }
    return acc;
  }, []);
};

export const sortSlotsByDateAndTime = (tagList = []) => {
  if (!Array.isArray(tagList)) return [];
  const getTimeValue = (value) => {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  };
  const list = tagList.slice();
  list.sort((a, b) => getTimeValue(a?.date) - getTimeValue(b?.date));
  return list.map((day) => ({
    ...day,
    times: (day?.times || [])
      .slice()
      .sort(
        (t1, t2) => getTimeValue(t1?.startTime) - getTimeValue(t2?.startTime)
      ),
  }));
};

export function getCustomFontStyle(fontName, fontPath) {
  if (fontName && fontPath) {
    return `
      /* injected-custom-font */
      @font-face {
        font-family: '${fontName}';
        src: url('${fontPath}') format('truetype');
        font-display: swap;
      }
      .custom-font {
        font-family: '${fontName}', Arial, Helvetica, sans-serif !important;
      }
    `;
  }
  return `
    .custom-font {
      font-family: Arial, Helvetica, sans-serif !important;
    }
  `;
}

export const getGlobalFontInjectStyle = (fontName, fontUrl) => {
  if (!fontUrl) return "";
  const name = fontName || "CustomPreviewFont";
  return `
    <style>
      @font-face {
        font-family: '${name}';
        src: url('${fontUrl}') format('truetype');
        font-display: swap;
      }
      * { font-family: '${name}', Arial, Helvetica, sans-serif !important; }
      [style*="font-family"] { font-family: '${name}', Arial, Helvetica, sans-serif !important; }
    </style>
  `;
};

export const getGlobalFontResetStyle = () => {
  return `
    <style>
      * { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif !important; }
      [style*="font-family"] { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif !important; }
    </style>
  `;
};

export const buildHtmlWithFont = (baseHtml, { fontName, fontUrl, disableCustomFonts } = {}) => {
  if (fontUrl) {
    return `${baseHtml}${getGlobalFontInjectStyle(fontName, fontUrl)}`;
  }
  if (disableCustomFonts) {
    return `${baseHtml}${getGlobalFontResetStyle()}`;
  }
  return baseHtml;
};

const DEFAULT_TIME = "09:00:00";

export const getExecutionTimestampForTask = (task) => {
  const time = task?.executedTime || DEFAULT_TIME;
  return dayjs.utc(`${task.executionDate}T${time}Z`).toDate();
};

export const sortTasksByExecutionDateTime = (tasks) => {
  if (!Array.isArray(tasks)) return [];

  const hasMissingExecutionDate = tasks.some((task) => !task?.executionDate);
  if (hasMissingExecutionDate) return [];

  return (
    tasks?.slice().sort((a, b) => {
      const timestampA = getExecutionTimestampForTask(a).getTime();
      const timestampB = getExecutionTimestampForTask(b).getTime();
      return timestampA - timestampB; // earlier first
    }) || []
  );
};

export const getPlaceholderTextForFilters = (filter) => {
  const label = filter.label.toLowerCase();
  // Add 's' if label doesn't already end with 's'
  const pluralLabel = label.endsWith('s') ? label : `${label}s`;
  return `All ${pluralLabel}`;
};

export const resetCompanyDataOnLogin = (store) => {
  store.dispatch(apiSlice?.util?.resetApiState());
  store.dispatch(setCompanies({ companies: [] }));
  store.dispatch(setSelectedCompany({ selectedCompany: {} }));
};

export const HiringManagerPortalMissingLinkMessage = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div className="w-full max-w-lg bg-white border border-gray-200 rounded-lg p-6">
      <Heading type="h2" className="text-xl font-semibold text-gray-900">
        Looks like this link didn't work!
      </Heading>
      <Text className="text-sm text-gray-600 mt-2">
        No worries — this can happen when a link has expired or incomplete. Please open your latest Pending Actions email from 1Connect and use the most recent Hiring Manager portal link.
      </Text>
    </div>
  </div>
);
